import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RailDocument } from "shared/models/api/models";
import { AnnouncementOutlined, StarBorder, InfoOutlined, Star } from '@mui/icons-material';
import { GridLabel, SortStyle, FilterArrow, ISortConfig, SortArrow, GridRulebookLabel } from './GridComponents';
import useSortableData from './GridComponents';
import { ConfirmationBox, IConfirmProps } from 'components/ui/ConfirmationBox';
import { IFilterConfig, IPageConfig } from './GridComponents';
import './grid-cards.css';
import { MarkChatRead } from "components/menu/Labels";
import { ViewInfo } from "./documents/DocumentInfo/DocumentInfo";
import { Grid2, Hidden } from "@mui/material";
import { Checkbox } from "../ui/LabelledCheckbox";
import {
    AddDocumentAction, DeleteDocumentAction, DeletePermanentlyDocumentAction, DocumentStatuses, DocumentStatusIcon,
    DocumentTypeIcon, EditDocumentAction, FavouriteDocumentAction, IDocumentAction, IDocumentFilter, RescindDocumentAction, DocumentViews
} from '../ui/DocumentRules';
import { Spinner } from '../ui/Spinner';
import { PageList } from "../ui/PageList";
import { setFavouriteFlagsAsync, updateFavouriteFlagAsync } from "../../shared/services/railDocumentsApi";
import { permanentDeleteDocument as permanentDeleteDocumentAsync, deleteDocument as deleteDocumentAsync } from 'shared/services/railDocumentsApi';
import { DocumentModuleEnum } from "shared/enums/document-module-enum"
import { formatDate } from "shared/models/mapping/mapping";

import { ReactComponent as PDFIcon } from "assets/icons/pdf-icon.svg";

export type GridColumnName = "Select" | "Favourite" | "SubType" | "Status";

interface IDocumentRowSharedProps {
    showSelectColumn: boolean,
    showFavouriteColumn: boolean,
    showSubTypeColumn: boolean,
    showStatusColumn: boolean,
    bookmarkAction?: (id: number) => void
}

interface IDocumentRowHeaderProps extends IDocumentRowSharedProps {
    requestSort: (x: string, y: SortStyle) => void,
    sortConfig: ISortConfig | null,
    categories: string[],
    statuses: string[],
    filter: (key: string, filterItems: Array<string>, createPredicate: (d: RailDocument) => (x: string) => boolean) => void
}

interface IFavouriteIconProps {
    documentId: number,
    isFavourite: boolean,
    setIsFavourite: (isFavourite: boolean) => void,
}

export const FavouriteIcon = (props: IFavouriteIconProps) => {
    const [working, setWorking] = useState(false);

    const toggleFavouriteFlag = async () => {
        if (!working) {
            setWorking(true);

            var result = await updateFavouriteFlagAsync(props.documentId, !props.isFavourite);

            if (result) {
                props.setIsFavourite(!props.isFavourite);
            }

            setWorking(false);
        }
    };

    const toggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();

        toggleFavouriteFlag();
    };

    return (
        <div className={"favourite-icon ccv" + (props.isFavourite ? " selected" : "") + (working ? " working" : "")}
            onClick={e => toggle(e)}>
            <div className="fill-width">
                {!props.isFavourite ? <StarBorder /> : <Star />}
            </div>
        </div>
    );
}

const DocumentRowActionColumn = (props: { acknowledgeRequired?: boolean }) =>
    <>{props.acknowledgeRequired ? <AnnouncementOutlined /> : <MarkChatRead />}</>;

interface IDocumentRowDetailProps extends IDocumentRowSharedProps {
    document: RailDocument,
    selected: boolean,
    documentSelectAction?: (id: number) => void,
    openDocument: (document: RailDocument) => void,
    setShowDocumentInfo: (id: number) => void
}

export const DocumentRow = (props: IDocumentRowDetailProps) => {
    const [isFavourite, setFavourite] = useState(props.document.isFavourite === true);

    const updateFavourite = (val: boolean) => {
        props.document.isFavourite = val;
        setFavourite(val);
    };

    return (
        <>
            <tr><td><div className="spacer"></div></td></tr>

            <DocumentRowTemplate
                className="document-list-item"
                onClick={() => { props.openDocument(props.document); }}
                actionClassName={props.document.acknowledgeRequired ? "acknowledge-required" : ""}
                action={<DocumentRowActionColumn acknowledgeRequired={props.document.acknowledgeRequired} />}
                favourite={<FavouriteIcon isFavourite={isFavourite} setIsFavourite={updateFavourite} documentId={props.document.id} />}
                favouriteVisible={props.showFavouriteColumn}
                status={<DocumentStatusIcon status={props.document.statusDescription} />}
                statusVisible={props.showStatusColumn ?? false}
                type={<GridLabel title="Type:" text={props.document.type!} />}
                title={<>{props.document.title}</>}
                documentNumber={<GridLabel title="Doc#:" text={props.document.documentNumber} />}
                published={<GridLabel title="Published:" text={formatDate(props.document.publishDate)} />}
                info={<InfoOutlined className="doc-info-icon" />}
                infoClicked={() => { props.setShowDocumentInfo(props.document.id) }}
                selectVisible={props.showSelectColumn ?? false}
                select={<Checkbox checkedValue={props.selected} />}
                selectClicked={() => { if (props.documentSelectAction) props.documentSelectAction(props.document.id) }}
                rulebook={<GridRulebookLabel cbtc={props.document.cbtc} traditional={props.document.traditional} />}
            />
        </>);
};

interface IDocumentRowTemplateProps {
    className: string,
    onClick?: () => void,
    action: ReactNode,
    actionClassName?: string,
    favourite: ReactNode,
    favouriteVisible: boolean,
    favouriteClicked?: () => void,
    //icon: ReactNode,
    title: ReactNode,
    documentNumber: ReactNode,
    published: ReactNode,
    type: ReactNode,
    info: ReactNode,
    infoClicked?: () => void,
    select: ReactNode,
    selectClicked?: () => void,
    selectVisible: boolean,
    status: ReactNode,
    statusClicked?: () => void,
    statusVisible: boolean,
    isHeader?: boolean,
    cbtc?: boolean,
    traditional?: ReactNode
    rulebook?: ReactNode
}

const DocumentRowTemplate = (props: IDocumentRowTemplateProps) => {
    const infoClicked = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
        if (props.infoClicked) {
            e.stopPropagation();
            props.infoClicked();
        }
    };

    const favouriteClicked = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
        if (props.favouriteClicked) {
            e.stopPropagation();
            props.favouriteClicked();
        }
    };

    const selectClicked = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
        if (props.selectClicked) {
            e.stopPropagation();
            props.selectClicked();
        }
    };

    const statusClicked = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
        if (props.statusClicked) {
            e.stopPropagation();
            props.statusClicked();
        }
    };

    return (
        <>
            <tr className={props.className} onClick={props.onClick}>
                <td className={"dl-col doc-action-col " + props.actionClassName}>{props.action}</td>
                {props.favouriteVisible &&
                    <td className="dl-col doc-favourite-col" onClick={favouriteClicked}>{props.favourite}</td>
                }
                {props.statusVisible &&
                    <td className="dl-col doc-status-col" onClick={statusClicked}>{props.status}</td>
                }

                {props.isHeader &&
                    <>
                        <td className={"dl-col doc-title-col"}>
                        <Grid2 container>
                            <Grid2 size={{ xs: 4, lg: 3 }} className="dl-col doc-type-col">{props.type}</Grid2>
                            <Grid2 size={{ xs: 8, lg: 6 }}>{props.title}</Grid2>

                            <Hidden mdDown>
                                <Grid2 size={{ lg: 3 }} className={"dl-col doc-published-col"}>{props.published}</Grid2>
                            </Hidden>
                        </Grid2>
                        </td>
                    </>
                }

                {!props.isHeader &&
                    <td className="dl-col doc-title-col">
                        <Grid2 container>
                            <Grid2 size={{ xs: 3 }}>{props.rulebook}</Grid2>
                            <Grid2 size={{ xs: 8 }} className="doc-title-title">{props.title}</Grid2>
                            <Hidden mdDown>
                                <Grid2 size={{ lg: 3 }} className="dl-col doc-type-item">{props.type}</Grid2>
                            </Hidden>
                            <Grid2 size={{ xs: 8, lg:6 }} className="dl-col doc-number-item">{props.documentNumber}</Grid2>
                            <Grid2 size={{ xs: 8, lg: 3 }} className="dl-col doc-published-item">{props.published}</Grid2>
                        </Grid2>
                    </td>
                }

                <td className="dl-col doc-info-col" onClick={infoClicked}>{props.info}</td>

                <Hidden xsUp={!props.selectVisible}>
                    <td className="dl-col doc-select-col" onClick={selectClicked}>{props.select}</td>
                </Hidden>
            </tr>
        </>
    );
};

export interface ISortableColProps {
    className?: string,
    children?: ReactNode,
    onClick?: () => void,
    fieldName: string,
    sortStyle: SortStyle,
    sortFn: (field: string, style: SortStyle) => void,
    sortConfig: ISortConfig | null
}

export const SortableCol = (props: ISortableColProps) => {
    return (
        <span onClick={() => { props.sortFn(props.fieldName, props.sortStyle); }}>{props.children} <SortArrow fieldName={props.fieldName} config={props.sortConfig ?? {} as ISortConfig} /></span>
    );
};

interface IFilterableColProps {
    sortFn: (field: string, style: SortStyle) => void,
    fieldName: string,
    sortStyle: SortStyle,
    sortConfig: ISortConfig,
    children: ReactNode
}

export const FilterableCol = (props: IFilterableColProps) => {
    return (
        <span onClick={() => { props.sortFn(props.fieldName, props.sortStyle); }}>{props.children} <SortArrow fieldName={props.fieldName} config={props.sortConfig ?? {} as ISortConfig} /></span>
    );
};

interface IActionButtonProps {
    onClick: () => void,
    enabled: boolean,
    visible: boolean,
    action: IDocumentAction,
    preConfirmationMessageUpdate?: (confirmProps: IConfirmProps) => IConfirmProps
}

const ActionButton = (props: IActionButtonProps) => {
    const Icon = props.action.icon;

    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);

    const onClick = () => {
        if (props.enabled && props.onClick) {

            if (props.action.confirm) {
                var cp = { ...props.action.confirm };

                if (props.preConfirmationMessageUpdate) {
                    cp = props.preConfirmationMessageUpdate(cp);
                }

                cp.setResult = (result: string) => {
                    if (result === cp.defaultOption) {
                        props.onClick();
                    }

                    setConfirmProps(null);
                };

                setConfirmProps(cp);
            } else {
                props.onClick();
            }
        }
    }

    return (
        <td>
            <div onClick={onClick} className={"ab-item " + (props.enabled ? "" : "disabled ") + (props.visible ? "" : "hidden ") + props.action.className}>
                {Icon &&
                    <div className="ab-icon center-children-both"><Icon /></div>
                }
                <div className="ab-label center-children-both">{props.action.name}</div>
            </div>

            {confirmProps &&
                <ConfirmationBox {...confirmProps} />
            }
        </td>
    );
};

const DocumentHeaderRow = (props: IDocumentRowHeaderProps) =>
    <DocumentRowTemplate isHeader={true}
        className="document-list-header"
        action={<>Action</>}
        favourite={<></>}
        favouriteVisible={props.showFavouriteColumn}
        status={<FilterArrow text="Status" filterItems={props.statuses} onFilter={(x) => props.filter("statusDescription", x, d => (s) => d.statusDescription === s)} />}
        statusVisible={props.showStatusColumn ?? false}
        type={<FilterArrow text="Type" filterItems={props.categories} onFilter={(x) => props.filter("type", x, d => (s) => d.type === s)} />}
        title={<SortableCol fieldName="title" sortStyle={SortStyle.String} sortFn={props.requestSort} sortConfig={props.sortConfig}>Title</SortableCol>}
        documentNumber={<>Document Number</>}
        published={<SortableCol fieldName="publishDate" sortStyle={SortStyle.String} sortFn={props.requestSort} sortConfig={props.sortConfig}>Published</SortableCol>}
        info={<>Info</>}
        selectVisible={props.showSelectColumn ?? false}
        select={<>Select</>}
    />;

export interface IDocumentGridProps {
    documents?: RailDocument[] | null,
    onDataChanged?: () => void,
    bookmarkAction?: (id: number) => void,
    documentButtonAction?: (id: number) => void,
    documentSelectAction?: (id: number) => void,
    filterConfig?: IFilterConfig,
    selectedItems: number[] | null,
    hiddenColumns?: Array<GridColumnName>,
    pageSize?: number,
    hidePageList?: boolean,
    noDocumentsMessage?: string,
    filter?: IDocumentFilter | null,
    clearSelected: () => void,
    onModalsClosed?: () => void,
    onAddDocument?: () => void,
    onEditDocument?: (doc: RailDocument) => void,
    onRescindDocument?: (doc: RailDocument) => void,
    onDeleteDocument?: (docs: RailDocument[]) => void,
    onDeleteDocumentForever?: (docs: RailDocument[]) => void
}

export const DocumentGrid = (props: IDocumentGridProps) => {
    const [showDocumentInfo, setShowDocumentInfo] = useState(0);
    const [refresh, setRefresh] = useState(false);

    const sd_items = props.documents || [] as RailDocument[];
    const sd_sortConfig = {} as ISortConfig;
    const sd_filterConfig = {} as IFilterConfig;
    const sd_pageConfig = props.pageSize ? {
        pageSize: props.pageSize,
        currentPage: 0,
        itemCount: props.documents?.length || 0
    } : null as IPageConfig | null;

    const { items, requestSort, sortConfig, requestFilter, requestPage, pageConfig } = useSortableData(
        sd_items,
        sd_sortConfig,
        sd_filterConfig,
        sd_pageConfig
    );

    const refreshGrid = (fullRefresh?: boolean) => {
        if (!fullRefresh) {
            requestPage(pageConfig?.currentPage ?? 1);
        } else {
            if (props.onDataChanged) props.onDataChanged()
        }
    };

    const categories = props.documents?.filter((item, i, arr) => arr.findIndex(t => t.type === item.type && t.type !== null) === i).map(x => x.type).sort() || [];
    const statuses = props.documents?.filter((item, i, arr) => arr.findIndex(t => t.statusDescription === item.statusDescription) === i).map(x => x.statusDescription).sort() || [];

    const filterFn = (
        key: string,
        filterItems: Array<string>,
        createPredicate: (d: RailDocument) => (x: string) => boolean
    ) => {
        const fn: any = (d: RailDocument) => {
            return filterItems.length > 0 ? (filterItems.find(createPredicate(d)) || "") !== "" : true;
        }

        requestFilter(key, fn);
    }

    const openDoc = (doc: RailDocument) => {
        if (props.documentButtonAction) {
            props.documentButtonAction(doc.id)
        }
    };

    const onDocInfoUpdate = () => {
        if (props.onDataChanged) props.onDataChanged();
    }

    const clearModals = () => {
        setShowDocumentInfo(0);
        setRefresh(!refresh);

        if (props.onModalsClosed) props.onModalsClosed();
    }

    useEffect(() => {
        requestPage(pageConfig?.currentPage || 0, props.pageSize);
    }, [props.pageSize]);

    const showColumn = (colName: GridColumnName): boolean => (!props?.hiddenColumns?.some(x => x === colName));

    const showSelectColumn = showColumn("Select");
    const showStatusColumn = showColumn("Status");
    const showFavouriteColumn = showColumn("Favourite");
    const showSubTypeColumn = showColumn("SubType");

    const getDocumentsForSelectedIndexes = (ids?: number[]) => {
        if (!props.documents) return [] as RailDocument[];

        return props.documents.filter(d => (ids ?? props.selectedItems ?? []).includes(d.id));
    };

    const getStatusesForAction = (action: IDocumentAction) => DocumentStatuses.filter(s => s.actions.includes(action));
    const getStatusIdsForAction = (action: IDocumentAction) => getStatusesForAction(action).map(status => status.id);

    const selectedDocumentsCanExecute = (action: IDocumentAction) => {
        const statusIds = getStatusIdsForAction(action);

        if (!props.selectedItems) return false;

        // are there any documents selected that have a status that is invalid for the action?
        return !getDocumentsForSelectedIndexes(props.selectedItems).some(d => !statusIds.includes(d.status));
    };

    const [canFavourite, setCanFavourite] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canRescind, setCanRescind] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canDeleteForever, setCanDeleteForever] = useState(false);

    useEffect(() => {
        var _favourite = false;
        var _edit = false;
        var _add = false;
        var _rescind = false;
        var _delete = false;
        var _deleteForever = false;

        if (props.filter?.rules) {
            const rules = props.filter.rules;

            // as long as the rule says we can add, we can. the selected does nothing
            _add = rules.canAdd === true;

            if (props.selectedItems && props.selectedItems.length > 0) {
                // some documents selected
                _favourite = rules.canFavourite === true && selectedDocumentsCanExecute(FavouriteDocumentAction);
                _deleteForever = rules.canDeleteForever === true && selectedDocumentsCanExecute(DeletePermanentlyDocumentAction);

                // single document actions
                if (props.selectedItems.length === 1) {
                    // one document selected 
                    const selectedDocumentId = props.selectedItems[0];
                    const selectedDocument = props.documents?.find(d => d.id === selectedDocumentId);

                    if (selectedDocument) {
                        const status = DocumentStatuses.find(status => status.id === selectedDocument.status);

                        if (status) {
                            _edit = (rules.canEdit === true) && status.actions.includes(EditDocumentAction);
                            _rescind = (rules.canRescind === true) && status.actions.includes(RescindDocumentAction) && selectedDocument.moduleId !== DocumentModuleEnum.Rulebook;
                            _delete = (rules.canDelete === true) && status.actions.includes(DeleteDocumentAction);
                        }
                    }
                }
            }
        }

        setCanFavourite(_favourite);
        setCanAdd(_add);
        setCanEdit(_edit);
        setCanRescind(_rescind);
        setCanDelete(_delete);
        setCanDeleteForever(_deleteForever);

    }, [props.selectedItems, props.documents]);

    const navigate = useNavigate();

    useEffect(() => {
        if (!props.filter) {
            return;
        }

        if (sortConfig) {
            requestSort(props.filter.defaultSortField ?? "title", SortStyle.String, props.filter.defaultSortOrder);
        }

        if (props.filter.redirectType) {
            navigate(props.filter.redirectType.url);
        }

    }, [props.filter]);

    const [msgBox, setMsgBox] = useState({} as IConfirmProps);
    const closeMessageBox = () => {
        setMsgBox({} as IConfirmProps);
    };
    const showMessageBox = (msg: string) => {
        const cp = {
            message: msg,
            options: ["OK"],
            defaultAction: "OK",
            setResult: closeMessageBox
        } as IConfirmProps;

        setMsgBox(cp);
    };

    const [actionsBusy, setActionsBusy] = useState(false);

    const favouriteSelectedAsync = async () => {
        if (!actionsBusy && canFavourite && props.selectedItems && props.selectedItems.length > 0) {
            const docs = getDocumentsForSelectedIndexes()
                .filter(d => d.isFavourite !== true);

            if (docs.length > 0) {
                setActionsBusy(true);

                await setFavouriteFlagsAsync(docs.map(d => d.id))
                    .then(result => {
                        docs.forEach(doc => {
                            doc.isFavourite = true;
                        });

                        setActionsBusy(false);

                        setMsgBox({
                            message: "Favourites updated. Would you like to clear the selected documents?",
                            options: ["Yes", "No"],
                            defaultOption: "Yes",
                            setResult: (response: string) => {
                                if (response === "Yes") {
                                    props.clearSelected();
                                }

                                closeMessageBox();
                            }
                        });

                        refreshGrid();
                    })
                    .catch(e => {
                        showMessageBox("There was an error updating your favourites.");
                        console.log(e);

                        setActionsBusy(false);
                    });
            } else {
                showMessageBox("All selected documents are already marked as favourite");
            }
        }
    };

    const addDocument = () => {
        if (canAdd && props.onAddDocument) {
            props.onAddDocument();
        }
    };

    const performActionOnCurrentDocument = (fn?: (doc: RailDocument) => void) => {
        if (!fn) return;

        if (props.selectedItems && props.selectedItems.length === 1) {
            const selectedDocumentId = props.selectedItems[0];
            const selectedDocument = props.documents?.find(d => d.id === selectedDocumentId);

            if (selectedDocument) {
                fn(selectedDocument);
            }
        }
    }

    const editSelected = () => {
        if (canEdit) {
            performActionOnCurrentDocument(props.onEditDocument);
        }
    };

    const rescindSelected = () => {
        if (canRescind) {
            performActionOnCurrentDocument(props.onRescindDocument);
        }
    };

    const deleteSelectedAsync = async () => {
        if (!actionsBusy && canDelete) {

            const docs = getDocumentsForSelectedIndexes();

            if (docs.length === 1) {
                setActionsBusy(true);

                const doc = docs[0];

                await deleteDocumentAsync(doc.id)
                    .then(response => {
                        setActionsBusy(false);
                        showMessageBox("Document has been moved to the Deleted folder");
                        if (props.onDataChanged) props.onDataChanged();
                    })
                    .catch(e => {
                        setActionsBusy(false);
                        showMessageBox("There was an error deleting the document.");
                        console.log(e);
                    });
            }
        }
    };

    const deleteForeverSelectedAsync = async () => {
        if (!actionsBusy && canDeleteForever) {
            const docs = getDocumentsForSelectedIndexes();

            if (docs.length === 1) {
                setActionsBusy(true);

                const doc = docs[0];

                await permanentDeleteDocumentAsync(doc.id)
                    .then(response => {
                        setActionsBusy(false);
                        showMessageBox("Your request has been successfully completed.");
                        if (props.onDataChanged) props.onDataChanged();
                    })
                    .catch(e => {
                        setActionsBusy(false);
                        showMessageBox("There was an error deleting the document.");
                        console.log(e);
                        return;
                    });
            }
        }
    };

    const addSelectedDocumentsToConfirmationMessage = (confirmProps: IConfirmProps): IConfirmProps => {
        const docs = getDocumentsForSelectedIndexes();

        confirmProps.message = <div className="delete-document-confirmation">
            <div>{confirmProps.message}</div>
            <table className="document-list">
                <tbody>
                    {docs.map(doc =>
                        <tr key={doc.id}>
                            <td><PDFIcon /></td>
                            <td>{doc.fileName}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>;

        return confirmProps;
    };

    return (
        <>
            <div>
                <ConfirmationBox {...msgBox} />
            </div>

            { props.filter &&
                <table className={"ab-list" + (actionsBusy ? " working" : "")}>
                    <tbody>
                        <tr>
                            <ActionButton action={AddDocumentAction}
                                visible={props.filter?.rules?.canAdd === true ? true : false}
                                onClick={addDocument}
                                enabled={canAdd} />

                            <ActionButton
                                action={EditDocumentAction}
                                visible={props.filter?.rules?.canEdit === true ? true : false}
                                onClick={editSelected}
                                enabled={canEdit} />

                            <ActionButton action={RescindDocumentAction}
                                visible={props.filter?.rules?.canRescind === true ? true : false}
                                onClick={rescindSelected}
                                preConfirmationMessageUpdate={addSelectedDocumentsToConfirmationMessage}
                                enabled={canRescind} />

                            <ActionButton action={DeleteDocumentAction}
                                visible={props.filter?.rules?.canDelete === true ? true : false}
                                onClick={deleteSelectedAsync}
                                enabled={canDelete} />

                            <ActionButton action={DeletePermanentlyDocumentAction}
                                visible={props.filter?.rules?.canDeleteForever === true ? true : false}
                                onClick={deleteForeverSelectedAsync}
                                preConfirmationMessageUpdate={addSelectedDocumentsToConfirmationMessage}
                                enabled={canDeleteForever} />
                        </tr>
                    </tbody>
                </table>
            }

            <table className="card-style document-list">

                {props.documents &&
                    <thead>
                        <DocumentHeaderRow
                            bookmarkAction={props.bookmarkAction}
                            requestSort={requestSort}
                            sortConfig={sortConfig}
                            categories={categories}
                            showSelectColumn={showSelectColumn}
                            showFavouriteColumn={showFavouriteColumn}
                            showSubTypeColumn={showSubTypeColumn}
                            showStatusColumn={showStatusColumn}
                            statuses={statuses}
                            filter={filterFn}
                        />
                    </thead>}

                <tbody>

                    {items?.length === 0 &&
                        <tr>
                            {props.documents &&
                                <td colSpan={12}>
                                    <div className="documents-no-resuts-message">{props.noDocumentsMessage}</div>
                                </td>
                            }
                            {!props.documents &&
                                <td colSpan={12}>
                                    <Spinner />
                                </td>
                            }
                        </tr>
                    }

                    {(items as RailDocument[]).map(doc => (
                        <DocumentRow key={doc.id}
                            document={doc}
                            showSelectColumn={showSelectColumn}
                            showFavouriteColumn={showFavouriteColumn}
                            showSubTypeColumn={showSubTypeColumn}
                            showStatusColumn={showStatusColumn}
                            bookmarkAction={props.bookmarkAction}
                            selected={
                                props.selectedItems
                                    ? props.selectedItems.filter(x => x === doc.id).length > 0
                                    : false
                            }
                            documentSelectAction={props.documentSelectAction}
                            openDocument={openDoc}
                            setShowDocumentInfo={setShowDocumentInfo}
                        />
                    ))}
                </tbody>
            </table>

            <br />

            <ViewInfo isOpen={showDocumentInfo > 0} onClose={clearModals} onUpdate={onDocInfoUpdate} documentId={showDocumentInfo} />
            {
                <div className="page-list">
                    {pageConfig && !props.hidePageList && props.documents &&
                        <PageList
                            pageNumber={pageConfig.currentPage + 1}
                            pageSize={pageConfig.pageSize}
                            totalResults={props.documents.length}
                            moveToPage={(page) => { requestPage(page - 1) }}
                        />
                    }
                </div>
            }
        </>
    );
}

export default DocumentGrid;
